import { useState } from "react";
import { ChatDisplayContainer } from "@shared/components/ChatDisplayContainer";
import { ChatHub } from "@shared/components/GroupChat/ChatHub";
import { useGlobalNavigationTarget } from "@shared/navigation/globalNavigation";
import type { AdminSection } from "../types/admin-panel";
import { useAdminPanel } from "../hooks/useAdminPanel";
import { AdminPanelSectionNav } from "../components/AdminPanelSectionNav";
import { AdminPanelHeader } from "../components/AdminPanelHeader";
import { UsersPanel } from "../components/UsersPanel";
import { PayrollPanel } from "../components/PayrollPanel";
import { AutoPayrollPanel } from "../components/AutoPayrollPanel";
import { ContractsPanel } from "../components/ContractsPanel";
import { VacationsPanel } from "../components/VacationsPanel";
import { AdminPanelChat } from "../components/AdminPanelChat";
import { ClockingPanel } from "../components/ClockingPanel";
import { SchedulesPanel } from "../components/SchedulesPanel";
import { StatisticsPanel } from "../components/StatisticsPanel";
import { AgendaWorkspace } from "@shared/components/Agenda/AgendaWorkspace";
import "../styles/admin-panel.css";

export function AdminPanelPage() {
  const [active, setActive] = useState<AdminSection>("usuarios");
  const [nominasMode, setNominasMode] = useState<"auto" | "manual">("auto");
  const [chatFloating, setChatFloating] = useState(true);

  useGlobalNavigationTarget("admin-panel", (target) => {
    switch (target.section) {
      case "usuarios":
      case "fichajes":
      case "horarios":
      case "agenda":
      case "nominas":
      case "contratos":
      case "vacaciones":
      case "estadisticas":
        setActive(target.section);
        break;
      default:
        break;
    }

    if (target.section === "nominas") {
      if (target.subsection === "auto" || target.subsection === "manual") {
        setNominasMode(target.subsection);
      }
    }
  });

  const {
    users,
    vacations,
    payrolls,
    contracts,
    vacationRejectionReasons,
    loading,
    clocking,
    clockingRange,
    setClockingRange,
    clockingRankPeriod,
    setClockingRankPeriod,
    rankingLoading,
    statistics,
    statisticsYear,
    setStatisticsYear,
    reload,
  } = useAdminPanel(active);

  return (
    <div className="page-shell">
      <div className="dashboard-shell-grid flex min-h-[720px] flex-col gap-6">
        <div className="dashboard-slide-left dashboard-delay-2">
          <AdminPanelSectionNav active={active} onChange={setActive} />
        </div>

        <section className="dashboard-component-shell dashboard-slide-right dashboard-delay-2 relative flex min-h-[720px] flex-col overflow-hidden rounded-[34px] transition-all duration-300 hover:-translate-y-1 hover:border-emerald-200 hover:shadow-[0_34px_90px_rgba(15,23,42,.14)]">
          <AdminPanelHeader
            active={active}
            onReload={reload}
            loading={loading}
          />

          <div className="min-h-0 flex-1 bg-white/55 p-5">
            {active === "usuarios" && (
              <UsersPanel users={users} loading={loading} onReload={reload} />
            )}

            {active === "fichajes" && (
              <ClockingPanel
                clocking={clocking}
                loading={loading}
                rankingLoading={rankingLoading}
                selectedRange={clockingRange}
                rankPeriod={clockingRankPeriod}
                onChangeRange={setClockingRange}
                onChangeRankPeriod={setClockingRankPeriod}
                onReload={reload}
              />
            )}

            {active === "horarios" && (
              <SchedulesPanel
                users={users}
                loading={loading}
                onReload={reload}
              />
            )}

            {active === "agenda" && (
              <AgendaWorkspace scope="admin" users={users} />
            )}

            {active === "nominas" && (
              <div className="space-y-5">
                <div className="flex gap-2">
                  <button
                    onClick={() => setNominasMode("auto")}
                    className={`rounded-2xl px-4 py-2 text-sm font-black transition ${
                      nominasMode === "auto"
                        ? "bg-slate-900 text-white"
                        : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    Generación automática (IA)
                  </button>
                  <button
                    onClick={() => setNominasMode("manual")}
                    className={`rounded-2xl px-4 py-2 text-sm font-black transition ${
                      nominasMode === "manual"
                        ? "bg-slate-900 text-white"
                        : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    Subida manual
                  </button>
                </div>

                {nominasMode === "auto" ? (
                  <AutoPayrollPanel users={users} />
                ) : (
                  <PayrollPanel
                    users={users}
                    payrolls={payrolls}
                    loading={loading}
                    onReload={reload}
                  />
                )}
              </div>
            )}

            {active === "contratos" && (
              <ContractsPanel
                users={users}
                contracts={contracts}
                loading={loading}
                onReload={reload}
              />
            )}

            {active === "vacaciones" && (
              <VacationsPanel
                vacations={vacations}
                rejectionReasons={vacationRejectionReasons}
                loading={loading}
                onReload={reload}
              />
            )}

            {active === "estadisticas" && (
              <StatisticsPanel
                statistics={statistics}
                selectedYear={statisticsYear}
                loading={loading}
                onChangeYear={setStatisticsYear}
              />
            )}
          </div>
        </section>
      </div>

      <ChatDisplayContainer
        floating={chatFloating}
        label="Chat de administración"
        onChangeFloating={setChatFloating}
      >
        <ChatHub
          scope="admin"
          floating={chatFloating}
          direct={<AdminPanelChat floating={chatFloating} />}
        />
      </ChatDisplayContainer>
    </div>
  );
}
